import React, { useEffect, useState } from 'react'
import { cx } from '@/utils/strings'

type StaggerListProps = {
  children: React.ReactNode
  className?: string
  isVisible?: boolean
  initial?: 'hidden' | 'visible'
  reverseDirection?: boolean
  staggerDelay?: number
}

type StaggerListItemProps = {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

export function StaggerListItem({ children, className, style }: StaggerListItemProps) {
  return (
    <li
      className={cx(
        'transition-[opacity,translate] duration-200 ease-anticipate',
        '[.stagger-list.is-hidden_&]:translate-y-[10px] [.stagger-list.is-hidden_&]:opacity-0',
        '[.stagger-list.is-visible_&]:translate-y-0 [.stagger-list.is-visible_&]:opacity-100',
        className
      )}
      style={style}
    >
      {children}
    </li>
  )
}

export default function StaggerList({
  children,
  className,
  isVisible = false,
  initial = 'hidden',
  reverseDirection = false,
  staggerDelay = 100,
  ...rest
}: StaggerListProps) {
  const [visibleState, setVisibleState] = useState(initial === 'visible')

  useEffect(() => {
    if (isVisible !== visibleState) {
      setVisibleState(isVisible)
    }
  }, [isVisible])

  const childArray = React.Children.toArray(children)
  const totalChildren = childArray.length

  return (
    <ul className={cx('stagger-list', visibleState ? 'is-visible' : 'is-hidden', className)} {...rest}>
      {childArray.map((child, index) => {
        const delay = reverseDirection ? (totalChildren - 1 - index) * staggerDelay : index * staggerDelay

        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            ...child.props,
            style: {
              ...child.props.style,
              transitionDelay: `${delay + 100}ms`,
            },
            key: child.key ?? index,
          } as any)
        }
        return child
      })}
    </ul>
  )
}
